import type { Calendar, CalendarException, CalendarId, Project } from '@bigint-project/core';
import { digestManagedExceptions } from './canonical.js';
import type { CalendarPin, CatalogDigest } from './contracts.js';
import { fail } from './errors.js';
import { digestProjectCalendarBasis } from './materialize.js';
import { isSyscalId, parseSyscalId } from './syscal-id.js';
import { compareStableText } from './stable.js';

function pinsForCalendar(pins: readonly CalendarPin[], calendarId: CalendarId): readonly CalendarPin[] {
  return pins.filter((pin) => pin.projectCalendarId === calendarId);
}

async function validateCalendar(
  project: Project,
  calendar: Calendar,
  pins: readonly CalendarPin[],
  digest: CatalogDigest,
): Promise<void> {
  const owned = new Map<string, CalendarPin>();
  for (const pin of pinsForCalendar(pins, calendar.id)) {
    for (const id of pin.managedExceptionIds) {
      if (owned.has(id)) fail('SYSCAL_MANAGED_CONTENT_DRIFT', `System exception is owned by more than one pin: ${id}`);
      const parsed = parseSyscalId(id);
      if (parsed.sourceKey !== pin.sourceKey || parsed.year !== pin.year) {
        fail('SYSCAL_MANAGED_CONTENT_DRIFT', `Pin ${pin.id} owns an out-of-scope system exception: ${id}`);
      }
      owned.set(id, pin);
    }
  }
  const ids = new Set<string>();
  let encounteredSystem = false;
  let previousSystemId = '';
  const materializedByPin = new Map<string, CalendarException[]>();
  for (const exception of calendar.exceptions) {
    if (ids.has(exception.id)) fail('SYSCAL_MANAGED_CONTENT_DRIFT', `Duplicate calendar exception id: ${exception.id}`);
    ids.add(exception.id);
    if (!isSyscalId(exception.id)) {
      if (encounteredSystem) fail('SYSCAL_ORDER_INVALID', 'Manual calendar exceptions must precede system exceptions');
      continue;
    }
    encounteredSystem = true;
    if (compareStableText(exception.id, previousSystemId) < 0) {
      fail('SYSCAL_ORDER_INVALID', 'System calendar exceptions must be sorted by stable id');
    }
    previousSystemId = exception.id;
    const pin = owned.get(exception.id);
    if (pin === undefined) fail('SYSCAL_UNOWNED_ID', `Unowned reserved system calendar exception: ${exception.id}`);
    const list = materializedByPin.get(pin.id) ?? [];
    list.push(exception);
    materializedByPin.set(pin.id, list);
  }
  for (const [id] of owned) {
    if (!ids.has(id)) fail('SYSCAL_MANAGED_CONTENT_DRIFT', `Managed system exception is missing: ${id}`);
  }
  for (const pin of pinsForCalendar(pins, calendar.id)) {
    const actual = materializedByPin.get(pin.id) ?? [];
    if (actual.length !== pin.managedExceptionIds.length
      || await digestManagedExceptions(actual, digest) !== pin.managedPayloadSha256
      || await digestProjectCalendarBasis(project, calendar.id, digest) !== pin.calendarBasisSha256) {
      fail('SYSCAL_MANAGED_CONTENT_DRIFT', `Managed system calendar content drifted for pin ${pin.id}`);
    }
  }
}

export async function validateSyscalIntegrity(
  project: Project,
  pins: readonly CalendarPin[],
  digest: CatalogDigest,
): Promise<void> {
  const pinIds = new Set<string>();
  const scopes = new Set<string>();
  for (const pin of pins) {
    if (pinIds.has(pin.id)) fail('SYSCAL_MANAGED_CONTENT_DRIFT', `Duplicate calendar pin id: ${pin.id}`);
    pinIds.add(pin.id);
    const scope = `${pin.projectCalendarId}\u0000${pin.sourceKey}\u0000${pin.year}`;
    if (scopes.has(scope)) fail('CALENDAR_PIN_ALREADY_EXISTS', `Duplicate active calendar pin scope: ${scope}`);
    scopes.add(scope);
    if (!project.calendars.some((calendar) => calendar.id === pin.projectCalendarId)) {
      fail('CALENDAR_NOT_FOUND', `Pinned calendar not found: ${pin.projectCalendarId}`);
    }
  }
  for (const calendar of project.calendars) await validateCalendar(project, calendar, pins, digest);
}

export function assertNoReservedManualId(exception: CalendarException): void {
  if (isSyscalId(exception.id)) {
    fail('SYSCAL_RESERVED_ID', `Manual exception cannot use the reserved syscal namespace: ${exception.id}`);
  }
}
